import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import styles from "./staff.module.scss";

/*components*/
import StaffCard from "./components/StaffCard";
import BackButton from "./components/BackButton";
import PageHeader from "./components/PageHeader";
import EmptyStaffList from "./components/EmptyStaffList";
import PmsMembersAlert from "./components/PmsMembersAlert";
import constants from "./../../constants";
import { decodeId, encodeId, getStorage, setStorage } from "utils";

import StickySidebar from "accountOwner/pages/Scheduler/components/StickySidebar";
import SearchBar from "accountOwner/pages/Notes/component/Filters/SearchBar";
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from "react-accessible-accordion";
import Card from "components/Card";
import ReportsToCheckbox from "./components/ReportsToCheckbox";
import FilterMemberType, {
  allMembersValue,
} from "./components/FilterMemberType";
import SidebarStaffRoleFilter from "./components/SidebarStaffRoleFilter";
import SidebarDepartmentFilter from "./components/SidebarDepartmentFilter";
import Loader from "components/Loader";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import SidebarOfficeFilter from "./components/SidebarOfficeFilter";
import NotAccessible from "accountOwner/pages/ManagerDashboard/components/NotAccessible";
import { useStaffDataFetchingMethods } from "./staff.hooks";
import CustomDropdown from "components/Dropdown";
import checkIcon from "assets/images/ico-check.svg";

const filterInitial = {
  designationIds: [],
  type: [allMembersValue],
  officeIds: [],
  departmentIds: [],
  reportToIds: [],
  sortBy: 0,
  sortOrder: 0,
  sortId: "default",
};

const Staff = ({ t }) => {
  const history = useHistory();
  const params = useParams();
  const location = useLocation();
const canSelectRef=useRef(new Map());
  const globalStaffPage = useMemo(
    () => location.pathname.includes("global-staff"),
    [location.pathname],
  );
  const {
    selectedId,
    isAdmin: isAdminUnderOwner,
    isStaff,
    profile,
    isOwner,
    dataAccessValues,
  } = useAccountOwnerCommon();

  const {
    globalStaffMembers,
    globalStaffMembersLoading,
    searchType,
    exportLoading,
    initializeStaffData,
    fetchMoreData,
    downloadAllExportFile: downloadAllExportFileFromHook,
    handleSearchFilter: handleSearchFilterFromHook,
    dispatch,
    setGlobalStaffMembers,
  } = useStaffDataFetchingMethods({ t });

  const staffMembers = useSelector((state) => state.staff.staffMembers);
  const isLoading = useSelector((state) => state.staff.isLoading);
  const officeDetail = useSelector((state) => state.offices.officeDetail);
  const [selectedStaff, setSelectedStaff] = useState({
    data: [],
    mappedData: new Map(),
  });
  const [isSidebarActive, setIsSidebarActive] = useState(true);
  const [allFilters, setAllFilters] = useState(() => {
    const filters = getStorage(
      globalStaffPage
        ? constants.accordion.cache.globalStaffListing
        : constants.accordion.cache.staffListing,
    );
    return filters ? { ...filterInitial, ...filters } : { ...filterInitial };
  });
  const [resetFilters, setResetFilters] = useState(false);
  const allFiltersRef = useRef({ ...filterInitial });
  const canAccessThisPage =
    (globalStaffPage && (isOwner || (isStaff && dataAccessValues.isAdmin))) ||
    !globalStaffPage;
  const cannnotAccessThiPage = !canAccessThisPage;

  const selectedOwnerId = globalStaffPage ? selectedId : officeDetail?.ownerId;
  const officeId = decodeId(params.id);
  const officeActive = officeDetail?.isActive;
  const isAdmin = useMemo(
    () => (globalStaffPage ? isAdminUnderOwner : !!location.state?.isAdmin),
    [location.state?.isAdmin, globalStaffPage, isAdminUnderOwner],
  );
  const resetApi = () => {
    setSelectedStaff({
      data: [],
      mappedData: new Map(),
    });
    initializeStaffData(searchType, allFilters);
  };
  // Initialize staff
  useEffect(() => {
    initializeStaffData("", allFilters);
  }, [
    allFilters,
    isAdmin,
    selectedOwnerId,
    globalStaffPage,
    isStaff,
    initializeStaffData,
    canAccessThisPage
  ]);

  const downloadAllExportFile = useCallback(
    (type, ids) => {
      downloadAllExportFileFromHook(type, allFilters, searchType, ids);
    },
    [downloadAllExportFileFromHook, allFilters, searchType],
  );

  const goBackToStaffOfficeAdmin = useCallback(() => {
    history.goBack();
  }, [history]);
  const redirectToPmsMembers = useCallback(() => {
    history.push({
      pathname: constants.routes.pmsMembers.replace(
        ":officeId",
        encodeId(parseInt(officeId)),
      ),
      state: location.state,
    });
  }, [history, officeId, location.state]);
  const handleFetchMoreData = useCallback(() => {
    if(!canAccessThisPage) return;
    fetchMoreData(allFilters);
  }, [fetchMoreData, allFilters, canAccessThisPage]);
  const handleSearchFilter = useCallback(
    (e) => handleSearchFilterFromHook(e, allFilters),
    [handleSearchFilterFromHook, allFilters],
  );
  const handleSidebarToggle = useCallback(
    () => setIsSidebarActive((prev) => !prev),
    [],
  );

  const cacheString = useMemo(
    () => ({
      filters: globalStaffPage
        ? constants.accordion.cache.globalStaffListing
        : constants.accordion.cache.staffListing,
      accordion: globalStaffPage
        ? constants.accordion.cache.globalStaffListingAccordion
        : constants.accordion.cache.staffListingAccordion,
    }),
    [globalStaffPage],
  );

  const handleApplyFilter = () => {
    const value = { ...allFiltersRef.current };
    setAllFilters(value);
    setStorage(cacheString.filters, value);
  };

  const handleResetFliter = (anotherData = {}) => {
    setAllFilters({ ...filterInitial, ...anotherData });
    allFiltersRef.current = { ...filterInitial, ...anotherData };
    setStorage(cacheString.filters, {
      ...filterInitial,
      ...anotherData,
      selectedId: selectedOwnerId,
    });
    setResetFilters(true);
    setTimeout(() => setResetFilters(false), 200);
  };
const preservedRef=useRef(null)
useEffect(()=>{
  if(selectedId){
    if(preservedRef.current===null){
      preservedRef.current=selectedId;
    }else if(preservedRef.current!==selectedId){
      handleResetFliter();
      preservedRef.current=selectedId;
    }
  }
},[selectedId]);
  const filtersApplied = useMemo(
    () =>
      Object.entries(allFilters).some(([key, val]) =>
        key.includes("sort")
          ? false
          : key === "type"
            ? !val.includes(allMembersValue) && val.length > 0
            : val.length > 0,
      ),
    [allFilters],
  );
  const preExpanded = useMemo(
    () => getStorage(cacheString.accordion) || ["a"],
    [cacheString],
  );
  const staffList = useMemo(
    () => (globalStaffPage ? globalStaffMembers.data : staffMembers),
    [globalStaffPage, staffMembers, globalStaffMembers.data],
  );


  const getUniqueId = useCallback(
    (item) => (globalStaffPage ? item?.userId : item?.staffMemberId),
    [globalStaffPage],
  );
  const setterStaffList = useCallback(
    (data, index, updateOrDelete = true) => {
      if (globalStaffPage) {
        setGlobalStaffMembers((prev) => {
          if (updateOrDelete) {
            prev.data[index] = data;
          } else {
            prev.data.splice(index, 1);
          }
          return {
            ...prev,
          };
        });
      } else {
        dispatch({
          type: "STAFFMEMBERUPDATED",
          payload: { index, data, updateOrDelete },
        });
      }
      setSelectedStaff((prev) => {
        if (prev.data.length) {
          const uid = getUniqueId(data);
          const updateIndex = prev.mappedData.get(uid);
          if (updateIndex > -1) {
            prev.data[updateIndex] = data;
            return {
              ...prev,
            };
          } else {
            return prev;
          }
        }
      });
    },
    [globalStaffPage, dispatch, setGlobalStaffMembers, setSelectedStaff],
  );
  const handleSelectStaff = (event, onSelectData) => {
    setSelectedStaff((prev) => {
      const uid = getUniqueId(onSelectData);
      if (event.target.checked) {
        const valueIndex = prev.data.push(onSelectData);
        prev.mappedData.set(uid, valueIndex - 1);
        prev.data = prev.data.map((res) => res);
      } else {
        prev.data = prev.data.filter((res) => {
          const cuid = getUniqueId(res);
          return cuid !== uid;
        });
        prev.mappedData.delete(uid);
      }
      return { ...prev };
    });
  };
  const [rerender,setRerender]=useState(0);
useEffect(() => {
  setTimeout(() => {
    setRerender((prev) => prev + 1);
  }, 500);
}, [staffList]);
  const selecteId = useCallback((id) => {
    switch (id) {
      case "nameAscending":
        return {
          sortBy: 1,
          sortOrder: 1,
        };
      case "nameDescending":
        return {
          sortBy: 1,
          sortOrder: 2,
        };
      case "createdAscending":
        return {
          sortBy: 2,
          sortOrder: 1,
        };
      case "createdDescending":
        return {
          sortBy: 2,
          sortOrder: 2,
        };

      default:
        return {
          sortBy: 0,
          sortOrder: 0,
        };
    }
  }, []);
  const isAllSelected = useMemo(() => {
    return selectedStaff.data.length >0&&selectedStaff.data.length >= canSelectRef.current.size && selectedStaff.data.every((item)=>canSelectRef.current.has(getUniqueId(item)));
  }, [selectedStaff.data.length,rerender]);
  useEffect(() => {
     canSelectRef.current.clear();
  }, []);
  return (
    <div className="staff-listing-block scheduler-page">
      <div className="container">
        <BackButton onClick={goBackToStaffOfficeAdmin} />
        {(isLoading || exportLoading || globalStaffMembersLoading) && (
          <Loader />
        )}
        <PageHeader
          officeDetail={officeDetail}
          staffMembers={staffList}
          officeActive={officeActive}
          officeId={params.id}
          location={location}
          downloadAllExportFile={downloadAllExportFile}
          globalStaffPage={globalStaffPage}
          allSelectedData={selectedStaff.data}
          selectedId={selectedOwnerId}
          resetApi={resetApi}
        />
        {globalStaffPage ? null : (
          <PmsMembersAlert
            officeDetail={officeDetail}
            officeActive={officeActive}
            onRedirect={redirectToPmsMembers}
          />
        )}
        <Card className="marginTop30">
          <div className="scheduler-tabs-main-wrapper mt-0">
            <StickySidebar
              handleSidebarToggle={handleSidebarToggle}
              resetFilter={canAccessThisPage ? handleResetFliter : () => {}}
              isSidebarActive={isSidebarActive}
            >
              <span
                className={`position-relative ${styles["sidebar-heading"]}`}
              >
                {t("accountOwner.filters")}
                {canAccessThisPage && filtersApplied && (
                  <span className={"filter_red_dot"}></span>
                )}
              </span>
              <Accordion
                preExpanded={preExpanded}
                className="filter-accordion"
                allowZeroExpanded
                onChange={(e) => setStorage(cacheString.accordion, e)}
              >
                <AccordionItem uuid="a">
                  <AccordionItemHeading>
                    <AccordionItemButton>Status</AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <FilterMemberType
                      allFiltersRef={allFiltersRef}
                      resetFilters={resetFilters}
                      preSetter={allFilters.type}
                    />
                  </AccordionItemPanel>
                </AccordionItem>
                {globalStaffPage && (
                  <AccordionItem uuid="e">
                    <AccordionItemHeading>
                      <AccordionItemButton>Offices</AccordionItemButton>
                    </AccordionItemHeading>
                    <AccordionItemPanel>
                      <SidebarOfficeFilter
                        allFiltersRef={allFiltersRef}
                        resetFilters={resetFilters}
                        preSetter={allFilters.officeIds}
                      />
                    </AccordionItemPanel>
                  </AccordionItem>
                )}
                <AccordionItem uuid="b">
                  <AccordionItemHeading>
                    <AccordionItemButton>Roles</AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <SidebarStaffRoleFilter
                      allFiltersRef={allFiltersRef}
                      selectedId={selectedOwnerId}
                      resetFilters={resetFilters}
                      preSetter={allFilters.designationIds}
                    />
                  </AccordionItemPanel>
                </AccordionItem>
                <AccordionItem uuid="c">
                  <AccordionItemHeading>
                    <AccordionItemButton>Reporting To</AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel className={styles["sidebard-report-to"]}>
                    <ReportsToCheckbox
                      ownerId={selectedOwnerId}
                      officeId={
                        globalStaffPage ? allFilters.officeIds : [+officeId]
                      }
                      allFiltersRef={allFiltersRef}
                      resetFilters={resetFilters}
                      globalStaffPage={globalStaffPage}
                      preSetter={allFilters.reportToIds}
                    />
                  </AccordionItemPanel>
                </AccordionItem>
                <AccordionItem uuid="d">
                  <AccordionItemHeading>
                    <AccordionItemButton>Departments</AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <SidebarDepartmentFilter
                      allFiltersRef={allFiltersRef}
                      selectedId={selectedOwnerId}
                      resetFilters={resetFilters}
                      preSetter={allFilters.departmentIds}
                    />
                  </AccordionItemPanel>
                </AccordionItem>
              </Accordion>

              <div className="filter-btn-box filter-btn-box--sticky-footer">
                <button
                  className={"button button-round button-shadow mr-3"}
                  title={t("apply")}
                  onClick={handleApplyFilter}
                  disabled={cannnotAccessThiPage}
                >
                  {t("apply")}
                </button>
                <button
                  className={
                    "button button-round button-border button-dark reset-btn"
                  }
                  title={t("reset")}
                  onClick={() => handleResetFliter()}
                  disabled={cannnotAccessThiPage}
                >
                  {t("reset")}
                </button>
                <button
                  className={
                    "button button-round button-border button-dark cancel-btn"
                  }
                  title={t("close")}
                  onClick={handleSidebarToggle}
                >
                  {t("close")}
                </button>
              </div>
            </StickySidebar>

            <div className="tabs-right-col">
              {canAccessThisPage && (
                <div className="d-flex justify-content-between">
                  <SearchBar
                    parentClass=" bg-white ml-0"
                    searchValue={searchType}
                    handleSearchTerm={handleSearchFilter}
                    t={t}
                    placeholder={t("accountOwner.searchStaff")}
                  />
                  <CustomDropdown
                    options={[
                      {
                        id: "default",
                        name: "Default",
                      },
                      {
                        id: "nameAscending",
                        name: "Staff Member's Name (A-Z)",
                      },
                      {
                        id: "nameDescending",
                        name: "Staff Member's Name (Z-A)",
                      },
                      {
                        id: "createdAscending",
                        name: "Employment Date (Latest)",
                      },
                      {
                        id: "createdDescending",
                        name: "Employment Date (Oldest)",
                      },
                    ]}
                    selectedOption={0}
                    selectOption={(id) => {
                      const values = selecteId(id);
                      values.sortId = id;
                      handleResetFliter({ ...values });
                      initializeStaffData(searchType, {
                        ...allFilters,
                        ...values,
                      });
                    }}
                    defaultValue="Sort By"
                    renderAnyThing={(id) => {
                      if (allFilters.sortId === id) {
                        return (
                          <img src={checkIcon} alt="icon" className="mr-2" />
                        );
                      }
                      return <span className="d-inline me-md" />;
                    }}
                  />
                </div>
              )}
              <div className="data-list">
{cannnotAccessThiPage?null:                <div className="ch-checkbox c-field all-event-checkbox d-flex gap-sm mt-3">

                                  <label
                                    htmlFor="selectAll"
                                    className="position-relative"
                                  >
                                    <input
                                      type="checkbox"
                                      id="selectAll"
                                      checked={isAllSelected}
                                      onChange={() => {
                                        if (isAllSelected) {
                                          setSelectedStaff((prev) => {
                                            prev.mappedData.clear();
                                            return {
                                              ...prev,
                                              data: [],
                                            };
                                          });
                                        } else {
                                          setSelectedStaff((prev) => {
                                            const data = staffList.filter((item) =>{
                                              const uid = getUniqueId(item);
                                              if(canSelectRef.current.has(uid)){
                                                prev.mappedData.set(uid, uid);
                                                return true;
                                              }
                                              prev.mappedData.delete(uid);
                                              return false;
                                            });
                                            return {
                                              ...prev,
                                              data,
                                            };
                                          });
                                        }
                                      }}/>
                                    <span>Select All</span>
                                  </label>
                                </div>}
                <div className="row gutters-14">
                  {canAccessThisPage && staffList.length > 0
                    ? staffList.map((item, index, currArr) => {
                        const uid = getUniqueId(item);
                        return (
                          <StaffCard
                          key={uid}
                              data={item}
                              staffLogin={isStaff}
                              OfficeId={officeId}
                              location={location}
                              callApi={handleFetchMoreData}
                              lastItem={currArr.length - 1 === index}
                              globalStaffPage={globalStaffPage}
                              onSelect={handleSelectStaff}
                              detail={item}
                              isChecked={selectedStaff.mappedData.has(uid)}
                              officeId={officeId}
                              officeData={location.state?.officeData}
                              resetApi={resetApi}
                              setLoading={() => {}}
                              selectedId={selectedOwnerId}
                              index={index}
                              staffListAllData={staffList}
                              setterStaffList={setterStaffList}
                              canSelectRef={canSelectRef}
                            />
                        );
                      })
                    : canAccessThisPage &&
                      staffList.length === 0 &&
                      (!isLoading || !globalStaffMembersLoading) && (
                        <EmptyStaffList
                          profile={profile}
                          officeDetail={officeDetail}
                          officeActive={
                            officeActive ||
                            (globalStaffPage &&
                              (isOwner ||
                                (isStaff && dataAccessValues.isAdmin)))
                          }
                          officeId={params.id}
                          location={location}
                          globalStaffPage={globalStaffPage}
                        />
                      )}
                  {cannnotAccessThiPage && (
                    <div
                      className="w-100 not-accessible center_property"
                      style={{ height: "300px" }}
                    >
                      <NotAccessible Message={t("dashBoard.notAccessible")} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default withTranslation()(Staff);
